import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { openRazorpay } from "@/lib/razorpay";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Check, Loader2, CreditCard, ShieldCheck } from "lucide-react";

const PLANS = [
  {
    key: "free",
    name: "Free Trial",
    price: "₹0",
    sub: "forever",
    desc: "Try Jingle Hooks — 2 free 15-second jingles on the house.",
    features: ["30 credits (2 × 15s jingles)", "15-second max duration", "AdScript + Local Ads modules", "MP3 + WAV download"],
    cta: "Current plan",
    disabled: true,
  },
  {
    key: "payperuse",
    name: "Pay-per-use",
    price: "₹799",
    sub: "one-time",
    desc: "Buy 40 credits — enough for one 30-sec jingle plus bonus 10s.",
    features: ["40 credits (~1 × 30s + buffer)", "All durations up to 60s", "All 4 modules unlocked", "All genres, moods, languages"],
    cta: "Buy 40 credits",
    highlight: false,
  },
  {
    key: "subscription",
    name: "Pro",
    price: "₹7,999",
    sub: "per month",
    desc: "For agencies & growth teams running constant campaigns.",
    features: ["450 credits / month (~15 × 30s)", "Unused credits roll over forever", "Up to 60-second anthems", "Brand Anthem module unlocked", "Priority generation", "Commercial license"],
    cta: "Subscribe",
    highlight: true,
  },
];

export default function Pricing() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [paying, setPaying] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const openCheckout = (plan) => {
    if (!user) {
      navigate("/login");
      return;
    }
    setSelectedPlan(plan);
    setOpen(true);
  };

  const handlePay = async () => {
    if (!selectedPlan) return;
    setPaying(true);
    try {
      const result = await openRazorpay({
        purpose: selectedPlan.key,
        onSuccess: () => {
          refresh();
          setOpen(false);
          navigate("/dashboard");
        },
      });
      if (!result) setOpen(false);
    } finally {
      setPaying(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="pricing-page">
      <Navbar />

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="max-w-2xl mb-14">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500 mb-4">
            Pricing
          </div>
          <h1 className="font-display text-5xl md:text-6xl font-semibold tracking-tighter leading-[1] text-balance">
            Simple pricing. <br />
            <span className="text-[#FF4500]">Unfair output.</span>
          </h1>
          <p className="mt-5 text-lg text-zinc-600">
            Start free with 2 jingles on the house. Scale as your campaigns grow.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {PLANS.map((p) => (
            <div
              key={p.key}
              className={`p-8 border relative ${
                p.highlight
                  ? "bg-[#09090B] text-white border-black"
                  : "bg-white text-black border-black/10"
              }`}
              data-testid={`plan-card-${p.key}`}
            >
              {p.highlight && (
                <div className="absolute -top-3 left-8 bg-[#FF4500] text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1">
                  Most popular
                </div>
              )}
              <div className="font-display text-2xl font-semibold mb-2">
                {p.name}
              </div>
              <div className={`text-sm mb-6 ${p.highlight ? "text-white/60" : "text-zinc-600"}`}>
                {p.desc}
              </div>
              <div className="flex items-end gap-2 mb-6">
                <span className="font-display text-5xl font-semibold tracking-tighter">
                  {p.price}
                </span>
                <span className={`mb-2 ${p.highlight ? "text-white/60" : "text-zinc-500"}`}>
                  {p.sub}
                </span>
              </div>
              <Button
                onClick={() => openCheckout(p)}
                disabled={p.disabled}
                className={`w-full rounded-sm h-11 font-semibold ${
                  p.highlight
                    ? "bg-[#FF4500] hover:bg-[#E03C00] text-white"
                    : "bg-black hover:bg-zinc-800 text-white"
                } ${p.disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                data-testid={`plan-cta-${p.key}`}
              >
                {p.cta}
              </Button>

              <div className={`mt-8 pt-6 border-t ${p.highlight ? "border-white/10" : "border-black/10"} space-y-3`}>
                {p.features.map((f, i) => (
                  <div key={i} className="flex items-start gap-3 text-sm">
                    <Check size={16} className="text-[#FF4500] mt-0.5 shrink-0" />
                    <span>{f}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-14 p-5 border border-dashed border-black/20 flex items-center gap-3 text-sm text-zinc-600">
          <ShieldCheck size={18} className="text-[#FF4500]" />
          All plans include royalty-free commercial use. Download in MP3 or WAV.
        </div>

        <div className="mt-6 p-5 border border-black/10 bg-[#F8F9FA] flex flex-wrap items-center justify-between gap-4" data-testid="topup-cta">
          <div>
            <div className="font-display text-xl font-semibold">Need just a few extra credits?</div>
            <div className="text-sm text-zinc-600 mt-1">Top up anytime from ₹300 — ₹20 per credit, no subscription.</div>
          </div>
          {user ? (
            <Link to="/dashboard" data-testid="topup-pricing-link">
              <Button className="bg-black hover:bg-zinc-800 text-white rounded-sm h-10 px-5">
                Top up credits
              </Button>
            </Link>
          ) : (
            <Link to="/login">
              <Button className="bg-black hover:bg-zinc-800 text-white rounded-sm h-10 px-5">
                Sign in to top up
              </Button>
            </Link>
          )}
        </div>
      </main>

      {/* Mock Razorpay checkout dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-sm p-0 border-black/10" data-testid="checkout-dialog">
          <div className="p-6">
            <DialogHeader className="mb-1">
              <DialogTitle className="font-display text-2xl font-semibold tracking-tight flex items-center gap-2">
                <CreditCard size={20} className="text-[#FF4500]" />
                Checkout
              </DialogTitle>
            </DialogHeader>
            {selectedPlan && (
              <>
                <div className="text-sm text-zinc-500 mb-5">
                  Complete payment via Razorpay (demo — no real charge).
                </div>
                <div className="bg-[#F8F9FA] border border-black/10 p-4 mb-5">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-zinc-500">Plan</span>
                    <span className="font-semibold">{selectedPlan.name}</span>
                  </div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-zinc-500">Billing</span>
                    <span className="font-semibold">{selectedPlan.sub}</span>
                  </div>
                  <div className="h-px bg-black/10 my-3" />
                  <div className="flex justify-between items-end">
                    <span className="text-sm text-zinc-500">Total</span>
                    <span className="font-display text-3xl font-semibold tracking-tighter">
                      {selectedPlan.price}
                    </span>
                  </div>
                </div>
                <Button
                  onClick={handlePay}
                  disabled={paying}
                  className="w-full bg-[#FF4500] hover:bg-[#E03C00] text-white rounded-sm h-11 font-semibold"
                  data-testid="confirm-pay-btn"
                >
                  {paying ? (
                    <><Loader2 size={16} className="mr-2 animate-spin" /> Opening Razorpay…</>
                  ) : (
                    `Pay ${selectedPlan.price}`
                  )}
                </Button>
                <div className="mt-4 text-center font-mono text-[10px] uppercase tracking-widest text-zinc-400">
                  🔒 Secured by Razorpay · UPI · Cards · Netbanking
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
